import { captureMessage } from '@sentry/vue'
import { useToast } from '@/shared/composables/UseToast'

class ServerError extends Error {
  response?: { status: number }

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ServerError'
    if (status)
      this.response = { status }
  }
}

type ErrorResponse = {
  status: number
  message: string
  errors: Record<string, string[]>
}

/**
 * Map HTTP Status Codes to handlers, so we have a generic way of handling certain errors.
 */
const handlers: {
  codes: number[]
  handler: (code: number, error: string) => Promise<void>
}[] = [
  {
    codes: [
      400,
      401,
      403,
      404,
      409,
    ],
    handler: async (code, error) => {
      const toast = useToast()

      toast.error(`Foutmelding (${code})`, error)
    },
  },
  {
    codes: [ 502, 503 ],
    handler: async () => {
      const toast = useToast()

      // server overload
      toast.error('De server is te druk', 'De server is momenteel overbelast. Probeer het later opnieuw.')
    },
  },
]

/**
 * Shared function to handle errors from an API. Like Rendering error messages.
 */
export async function handleErrorResponse(
  request: RequestInit,
  response: Response,
  overrideToast?: ReturnType<typeof useToast>,
): Promise<never> {
  const toast = overrideToast ?? useToast()

  if (response.status === 500) { // Handle server errors, cannot be handled by the client
    toast.error('Er is een server fout opgetreden', 'Het verzoek kon niet worden verwerkt. Het development team, is op de hoogte gebracht.')

    return Promise.reject(new ServerError('Server error', response.status))
  }

  let responseBody: ErrorResponse

  try {
    responseBody = await response.json()
  } catch {
    toast.error('Er is een fout opgetreden. Probeer het later opnieuw.')

    return Promise.reject(new ServerError('Invalid JSON response', response.status))
  }

  const errorCodes = responseBody.errors

  if (!errorCodes || Object.keys(errorCodes).length === 0) {
    toast.error('Er is een fout opgetreden. Probeer het later opnieuw.')
    captureMessage(`No error messages found for status ${response.status}`, {
      extra: {
        request: request,
        response: responseBody,
        status: response.status,
        url: response.url,
      },
    })

    return Promise.reject(new ServerError('No error messages found', response.status))
  }

  /*
   * The keys of `errors` are internal failure codes, not HTTP status codes — look up the handler
   * by the response's actual HTTP status instead. Multiple messages can be rendered, so we loop through them.
   */
  const handler = handlers.find(h => h.codes.includes(response.status))

  if (!handler) {
    toast.error('Er is een fout opgetreden. Probeer het later opnieuw.')
    captureMessage(`Unhandled status code: ${response.status}. For url: ${response.url}`)

    return Promise.reject(new ServerError('Unhandled status code', response.status))
  }

  for (const errors of Object.values(errorCodes)) {
    for (const error of errors) {
      await handler.handler(response.status, error)
    }
  }

  return Promise.reject(new ServerError('Client error', response.status))
}
